import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'
import { Grid } from '@/shared/ui/layouts/grid'

import { Children, type FC, createContext, useContext } from 'react'

import type {
  PageContentColumnProps,
  PageContentProps,
  PageContentSurfaces,
  PageContentVariant,
} from './types'

// Context pour partager le variant et les surfaces avec les sous-composants
type PageContentContextValue = {
  variant: PageContentVariant
  surfaces: PageContentSurfaces
  columnIndex: number
  inPageContent: boolean
}

export const PageContentContext = createContext<PageContentContextValue>({
  variant: 'single',
  surfaces: 'muted',
  columnIndex: 0,
  inPageContent: false,
})

export const usePageContentContext = () => {
  const context = useContext(PageContentContext)
  if (!context) {
    throw new Error('usePageContentContext must be used within a PageContent component')
  }
  return context
}

// Mapping des variants PageContent vers colonnes Grid
const variantToColumns: Record<PageContentVariant, '1' | '2' | '3'> = {
  single: '1',
  'two-1/2': '2',
  'two-67-33': '3',
}

// Classes de marqueur pour les variants (pour les tests)
const variantClass: Record<PageContentVariant, string | null> = {
  single: null,
  'two-1/2': 'page-content-two-half',
  'two-67-33': 'page-content-two-67-33',
}

// Extraire les surfaces individuelles à partir de la combinaison
const getSurfacesArray = (surfaces: PageContentSurfaces): ('plain' | 'muted')[] => {
  if (surfaces === 'plain' || surfaces === 'muted') {
    return [surfaces]
  }
  const [first, second] = surfaces.split('-') as ('plain' | 'muted')[]
  return [first, second]
}

/**
 * Content section for PageContent layouts.
 *
 * @example
 * <PageContent.Content>
 *   <main>Content area</main>
 * </PageContent.Content>
 */
const PageContentColumn: FC<PageContentColumnProps> = ({ children }) => {
  const { variant, surfaces, columnIndex } = useContext(PageContentContext)
  const surfacesArray = getSurfacesArray(surfaces)
  const surface = surfacesArray[columnIndex] || 'muted'

  // Pour two-67-33, la première colonne occupe 2 sur 3
  const colSpan = variant === 'two-67-33' && columnIndex === 0 ? '2' : '1'

  return (
    <Grid.Cell colSpan={colSpan}>
      <div
        className={cn('page-content-column', `page-content-column-${columnIndex}`, {
          'page-content-surface-muted': surface === 'muted',
          'page-content-surface-plain': surface === 'plain',
        })}
      >
        {children}
      </div>
    </Grid.Cell>
  )
}

PageContentColumn.displayName = 'PageContentColumn'

/**
 * PageContent root component.
 *
 * Layout container supporting single and two-column variants via the Compound Pattern.
 * Use `PageContent.Content` as children for layouts.
 *
 * @example
 * // Single column layout
 * <PageContent variant="single" surfaces="muted"  fullHeight>
 *   <PageContent.Content>Full-width content</PageContent.Content>
 * </PageContent>
 *
 * @example
 * // Two equal columns with different surfaces
 * <PageContent variant="two-1/2" surfaces="plain-muted">
 *   <PageContent.Content>Left column (plain)</PageContent.Content>
 *   <PageContent.Content>Right column (muted)</PageContent.Content>
 * </PageContent>
 *
 * @example
 * // Asymmetric columns (2:1) with same surface
 * <PageContent variant="two-67-33" surfaces="muted-muted">
 *   <PageContent.Content>Left main content (2/3, muted)</PageContent.Content>
 *   <PageContent.Content>Right sidebar (1/3, muted)</PageContent.Content>
 * </PageContent>
 */
const PageContentRoot: FC<PageContentProps> = ({
  variant = 'single',
  surfaces = 'muted',
  fullHeight = false,
  children,
}) => {
  const numberOfColumns = variantToColumns[variant]
  const blocks = getTypedBlocksByTypes<
    { PageContentColumn: PageContentColumnProps },
    ['PageContentColumn']
  >(children, ['PageContentColumn'])

  const { PageContentColumn: columns, ...unmatched } = blocks

  const hasColumns = columns.length > 0

  const normalizedChildren =
    variant === 'single' && !hasColumns ? (
      <PageContentColumn>
        <>{Object.values(unmatched)}</>
      </PageContentColumn>
    ) : (
      children
    )

  // Injecter l'index de colonne dans chaque enfant
  const childrenWithContext = Children.toArray(normalizedChildren).map((child, index) => (
    <PageContentContext.Provider
      key={index}
      value={{ inPageContent: true, variant, surfaces, columnIndex: index }}
    >
      {child}
    </PageContentContext.Provider>
  ))

  return (
    <div
      className={cn('page-content', variantClass[variant], {
        'page-content-full-height': fullHeight,
        [`page-content-surfaces-${surfaces}`]: true,
      })}
    >
      <Grid columns={numberOfColumns} gap="0">
        {childrenWithContext}
      </Grid>
    </div>
  )
}

PageContentRoot.displayName = 'PageContent'

/**
 * Multi-column layout component using the Compound Pattern.
 *
 * Supports 3 layout variants:
 * - `single`: single full-width column
 * - `two-1/2`: two equal columns (1:1)
 * - `two-67-33`: asymmetric columns (2:1)
 *
 * Surface combinations:
 * - Single column: `plain` or `muted`
 * - Two columns: `plain-plain`, `plain-muted`, `muted-plain`, `muted-muted`
 */
export const PageContent = Object.assign(PageContentRoot, {
  Column: PageContentColumn,
})
